import { useState, useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import { BaseDialog } from "../../../../../components/modals/BaseDialog";
import { AddVolumeForm } from "./AddVolumeForm";
import Button from "../../../../../components/Button";
import useNotification from "../../../../../hooks/useNotification";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";

export const EditVolumeModal = ({
  open,
  onClose,
  bipeds,
  initialVolumes,
  isSaving,
  onSave,
}) => {
  const { showNotification } = useNotification();
  const [volumes, setVolumes] = useState(initialVolumes);

  // Reseta o estado quando o modal abre
  useEffect(() => {
    if (open) {
      setVolumes(initialVolumes);
    }
  }, [open, initialVolumes]);

  const handleRemoveVolume = (vol) => {
    setVolumes((prev) => prev.filter((v) => v.identifier !== vol.identifier));
  };

  const handleRemoveItem = (volIdx, itemIdx, vol) => {
    setVolumes((prev) => {
      const newVols = [...prev];
      if (newVols[volIdx] && newVols[volIdx].items) {
        newVols[volIdx].items.splice(itemIdx, 1);
      }
      return newVols;
    });
  };

  const handleEditVolume = (identifier, newName) => {
    const trimmed = newName.trim();
    if (!trimmed) {
      showNotification("warning", "O nome do volume não pode ser vazio");
      return false;
    }

    const exists = volumes.some(
      (v) =>
        v.identifier !== identifier &&
        v.name.toLowerCase() === trimmed.toLowerCase(),
    );
    if (exists) {
      showNotification("warning", "Já existe um volume com este nome");
      return false;
    }

    setVolumes((prev) =>
      prev.map((v) =>
        v.identifier === identifier ? { ...v, name: trimmed } : v,
      ),
    );
    return true;
  };

  const handleSave = () => {
    onSave(volumes);
  };

  // Verifica se há itens não alocados
  const hasUnallocatedItems = useMemo(() => {
    const allocatedIdentifiers = new Set();

    // Coleta todos os identifies alocados (incluindo volumes bloqueados)
    volumes.forEach((vol) => {
      (vol.items || []).forEach((item) => {
        if (item.identify) {
          allocatedIdentifiers.add(item.identify);
        }
      });
    });

    // Verifica se TODOS os bipeds estão alocados
    const allIdentifiers = Object.keys(bipeds || {});
    return allIdentifiers.some((id) => !allocatedIdentifiers.has(id));
  }, [volumes, bipeds]);

  const hasLockedVolumes = initialVolumes.some((v) => v.hasShipment);

  const dialogActions = (
    <Box sx={{ display: "flex", gap: 2, my: 2, flexWrap: "wrap" }}>
      {hasLockedVolumes && (
        <Box
          sx={{
            color: "warning.main",
            fontSize: "0.875rem",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}>
          <FontAwesomeIcon icon={faLock} />
          Alguns volumes estão vinculados a carregamentos e não podem ser
          editados.
        </Box>
      )}
      {hasUnallocatedItems && (
        <Box
          sx={{
            color: "error.main",
            fontSize: "0.875rem",
            fontWeight: "bold",
            marginRight: "auto",
          }}>
          ⚠️ Existem itens fora de volumes. Aloque todos os itens antes de
          salvar.
        </Box>
      )}
      <Box sx={{ display: "flex", gap: 2, ml: "auto" }}>
        <Button
          text="Cancelar"
          onClick={onClose}
          className="btn btn-light fw-semibold"
          disabled={isSaving}
        />
        <Button
          text={isSaving ? "Salvando..." : "Salvar Alterações"}
          onClick={handleSave}
          className="btn btn-primary fw-semibold"
          disabled={isSaving || hasUnallocatedItems}
        />
      </Box>
    </Box>
  );

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title="Editar Volumes do Movimento"
      maxWidth={false}
      actions={dialogActions}
      sx={{
        "& .MuiDialog-paper": {
          width: "85vw",
          maxWidth: "none",
          height: "auto",
          maxHeight: "95vh",
        },
      }}>
      <Box sx={{ display: "flex", flexDirection: "column", height: "85vh" }}>
        <Box sx={{ flex: 1, overflow: "hidden" }}>
          <AddVolumeForm
            bipeds={bipeds}
            volumes={volumes}
            setVolumes={setVolumes}
            mode="edit"
            handleRemoveVolume={handleRemoveVolume}
            handleRemoveItem={handleRemoveItem}
            handleEditVolume={handleEditVolume}
          />
        </Box>
      </Box>
    </BaseDialog>
  );
};
